import { fetchMerchantDocument } from '../../services/merchant';

// Cached document URLs to avoid redundant API calls
const documentCache: Record<string, Promise<string | null>> = {};
// Store actual resolved URLs to avoid showing placeholder once fetched
const resolvedUrls: Record<string, string> = {};

// Use reliable placeholder images that won't fail to load
const NO_IMAGE_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23f1f1f1'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='16' text-anchor='middle' dominant-baseline='middle' fill='%23999'%3ENo Image Available%3C/text%3E%3C/svg%3E";
const NO_CERTIFICATE_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23f1f1f1'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='16' text-anchor='middle' dominant-baseline='middle' fill='%23999'%3EDownload the PDF to View%3C/text%3E%3C/svg%3E";
const PDF_ICON = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23f8f8f8'/%3E%3Cpath d='M117,80 L183,80 C186.3,80 189,82.7 189,86 L189,134 C189,137.3 186.3,140 183,140 L117,140 C113.7,140 111,137.3 111,134 L111,86 C111,82.7 113.7,80 117,80 Z' fill='%23e2e2e2'/%3E%3Cpath d='M150,88 L162,106 L150,106 L138,106 Z' fill='%23c0c0c0'/%3E%3Cpath d='M150,106 L162,106 L162,124 L138,124 L138,106 Z' fill='%23e74c3c'/%3E%3Ctext x='150' y='166' font-family='Arial' font-size='16' text-anchor='middle' fill='%23555555'%3EPDF Document%3C/text%3E%3C/svg%3E";

// Merchant interface for type safety
export interface MerchantDoc {
  _id?: string;
  mobileNo?: string;
  merchantId?: string;
  name?: string;
  kycDocs?: {
    shopFront?: string;
    shopInner?: string;
    evCer?: string;
  };
  kyc?: {
    docs?: boolean | {
      shopFront?: string;
      shopInner?: string;
      evCer?: string;
    };
  };
}

/**
 * Checks if a string is a full URL (starts with http:// or https://)
 */
const isFullUrl = (url: string): boolean => {
  return url.startsWith('http://') || url.startsWith('https://');
};

/**
 * Check if a URL is for a PDF
 */
export const isPdfUrl = (url: string): boolean => {
  return url.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('application/pdf') || url.toLowerCase().includes('content-type=pdf');
};

/**
 * Get image URL from merchant, with API fetch if needed
 * @param merchant Merchant object containing document information
 * @param type Document type to retrieve
 * @returns Image URL or placeholder if not available
 */
export const getImageUrl = (merchant: MerchantDoc | null, type: 'shopFront' | 'shopInner' | 'evCer'): string => {
  if (!merchant) return type === 'evCer' ? NO_CERTIFICATE_PLACEHOLDER : NO_IMAGE_PLACEHOLDER;

  const cacheKey = `${merchant.mobileNo}-${type}`;

  // 1. Return resolved URL if already cached
  if (resolvedUrls[cacheKey]) {
    return resolvedUrls[cacheKey]; // ✅ Always return actual URL
  }

  // 2. Check in kycDocs or kyc.docs
  const urlFromDocs =
    merchant.kycDocs?.[type] ||
    (typeof merchant.kyc?.docs === 'object' ? (merchant.kyc.docs as any)?.[type] : null);

  if (urlFromDocs && typeof urlFromDocs === 'string' && isFullUrl(urlFromDocs)) {
    resolvedUrls[cacheKey] = urlFromDocs;
    return urlFromDocs; // ✅ Directly return the actual document URL
  }

  // 3. If no cached/full URL, fetch from API
  if (!documentCache[cacheKey]) {
    documentCache[cacheKey] = (async () => {
      try {
        const token = localStorage.getItem('token');
        const url = await fetchMerchantDocument(merchant.mobileNo!, type, token, true);
        if (url) {
          resolvedUrls[cacheKey] = url;

          // Update any matching img tags if needed
          const images = document.querySelectorAll(`img[data-merchant-doc="${merchant.mobileNo}-${type}"]`);
          images.forEach(img => {
            (img as HTMLImageElement).src = url;
            img.setAttribute('data-is-pdf', isPdfUrl(url) ? 'true' : 'false');
            img.setAttribute('data-pdf-url', url);
          });

          return url;
        }
      } catch (err) {
        console.error(`[DEBUG] Error fetching document ${type}:`, err);
      }
      return null;
    })();
  }

  // If still not resolved, return placeholder
  return type === 'evCer' ? NO_CERTIFICATE_PLACEHOLDER : NO_IMAGE_PLACEHOLDER;
};




/**
 * Check if document exists in kycDocs
 * @param merchant Merchant object containing document information
 * @param type Document type to check
 * @returns Boolean indicating if document exists
 */
export const hasDocument = (merchant: MerchantDoc | null, type: 'shopFront' | 'shopInner' | 'evCer'): boolean => {
  if (!merchant) return false;
  
  // Check in kycDocs
  if (merchant.kycDocs && merchant.kycDocs[type]) {
    return true;
  }
  
  // Check in kyc.docs if it's an object
  if (merchant.kyc && merchant.kyc.docs && typeof merchant.kyc.docs === 'object') {
    const docsObj = merchant.kyc.docs as any;
    return !!docsObj[type];
  }
  
  return false;
}; 